import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import logger from '@widgets/logger';
import { ColorPicker } from '@components/ColorPicker';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { Typography } from '@components/Typography';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import TextEditor from '@components/TextEditor';
import { useTranslation } from '@hooks';
import { alertActions } from '@actions';
import { campaignsService } from '@services';
import { MB } from '@constants/file';
import { validateFileSize } from '@helpers/upload-file';
import { parseHtmlToEditorText, parseTipTapJSONToHTML } from '@helpers/text-editor';
import PostDynamicVariables from '@features/PostDynamicVariables';
import { PLACEHOLDER_CONTEXT } from '@constants/placeholders';
import { EMAIL_BLOCK_TYPE, EMAIL_BUTTON_BINDING, EMAIL_BUTTON_SHAPES } from '@constants/campaignTemplates';
import { BLOCK_LABEL_KEYS } from './designUtils';
import DynamicFieldsButton from './DynamicFieldsButton';

const Panel = styled('div')({
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
});

const PanelHeader = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
});

const ALIGN_OPTIONS = t => [
    { value: 'left', label: t('TemplateLibrary.alignLeft') },
    { value: 'center', label: t('TemplateLibrary.alignCenter') },
    { value: 'right', label: t('TemplateLibrary.alignRight') },
];

const SIZE_OPTIONS = [
    { value: 's', label: 'S' },
    { value: 'm', label: 'M' },
    { value: 'l', label: 'L' },
];

const OptionField = ({ id, label, value, onChange, options, disabled }) => (
    <div>
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block', mb: '4px' }}>
            {label}
        </Typography>
        <RadioButtonsGroup id={id} value={value} disabled={disabled} onChange={onChange} options={options} />
    </div>
);

const AlignField = ({ block, onChange, isView, t }) => (
    <OptionField
        id="pl-block-align"
        label={t('TemplateLibrary.alignment')}
        value={block.align || 'center'}
        disabled={isView}
        onChange={value => onChange({ align: value })}
        options={ALIGN_OPTIONS(t)}
    />
);

const BlockSettings = ({ block, styles, onChange, onBack, isView, otherLinkType }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [uploading, setUploading] = useState(false);

    const uploadImage = async e => {
        try {
            const file = e.target.files[0];
            if (!file) return;
            const { error, metric, size } = validateFileSize(file, 5 * MB);
            if (error) {
                dispatch(alertActions.error(t('apiErrors.file_size_too_large', { size, metric })));
                return;
            }
            setUploading(true);
            const data = new FormData();
            data.append('file', file);
            data.append('fileName', file.name);
            const response = await campaignsService.uploadEmailCampaignImage(data);
            onChange({ url: response.publicUrl });
        } catch (_) {
            logger.error("Couldn't upload template image");
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    // Survey and coupon links are mutually exclusive across the template — a campaign fills
    // exactly one destination kind at send.
    const bindingOptions = [
        { value: EMAIL_BUTTON_BINDING.URL, label: t('TemplateLibrary.bindingUrl') },
        {
            value: EMAIL_BUTTON_BINDING.SURVEY,
            label: t('TemplateLibrary.bindingSurvey'),
            disabled: otherLinkType === EMAIL_BUTTON_BINDING.COUPON,
        },
        {
            value: EMAIL_BUTTON_BINDING.COUPON,
            label: t('TemplateLibrary.bindingCoupon'),
            disabled: otherLinkType === EMAIL_BUTTON_BINDING.SURVEY,
        },
    ];

    return (
        <Panel>
            <PanelHeader>
                <IconButton size="small" onClick={onBack} aria-label={t('TemplateLibrary.backToBlocks')}>
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography variant="subtitle2">{t(BLOCK_LABEL_KEYS[block.type])}</Typography>
            </PanelHeader>

            {block.type === EMAIL_BLOCK_TYPE.HEADING && (
                <>
                    <Input
                        label={t('TemplateLibrary.headingText')}
                        fullWidth
                        disabled={isView}
                        inputProps={{
                            value: block.text || '',
                            onChange: e => onChange({ text: e.target.value }),
                        }}
                        addonWithoutWrapper
                        addonComponent={
                            <DynamicFieldsButton
                                disabled={isView}
                                onInsert={token => onChange({ text: `${block.text || ''}${token}` })}
                            />
                        }
                    />
                    <OptionField
                        id="pl-block-heading-level"
                        label={t('TemplateLibrary.headingLevel')}
                        value={String(block.level || 1)}
                        disabled={isView}
                        onChange={value => onChange({ level: Number(value) })}
                        options={[
                            { value: '1', label: 'H1' },
                            { value: '2', label: 'H2' },
                            { value: '3', label: 'H3' },
                        ]}
                    />
                    <ColorPicker
                        label={t('TemplateLibrary.textColor')}
                        fullWidth
                        disabled={isView}
                        inputProps={{
                            value: block.color || styles?.textColor || '',
                            onChange: e => onChange({ color: e?.target?.value ? e.target.value : e }),
                        }}
                    />
                    <AlignField block={block} onChange={onChange} isView={isView} t={t} />
                </>
            )}

            {block.type === EMAIL_BLOCK_TYPE.TEXT && (
                <>
                    <TextEditor
                        key={block.id}
                        label={t('TemplateLibrary.textContent')}
                        placeholderContext={PLACEHOLDER_CONTEXT.CAMPAIGN}
                        initialValue={parseHtmlToEditorText(block.html || '')}
                        editable={!isView}
                        onChange={json => onChange({ html: parseTipTapJSONToHTML(json) })}
                        toolbar={<PostDynamicVariables disabled={isView} />}
                    />
                    <OptionField
                        id="pl-block-text-size"
                        label={t('TemplateLibrary.fontSize')}
                        value={block.size || 'm'}
                        disabled={isView}
                        onChange={value => onChange({ size: value })}
                        options={SIZE_OPTIONS}
                    />
                    <ColorPicker
                        label={t('TemplateLibrary.textColor')}
                        fullWidth
                        disabled={isView}
                        inputProps={{
                            value: block.color || styles?.textColor || '',
                            onChange: e => onChange({ color: e?.target?.value ? e.target.value : e }),
                        }}
                    />
                    <AlignField block={block} onChange={onChange} isView={isView} t={t} />
                </>
            )}

            {block.type === EMAIL_BLOCK_TYPE.COLUMNS && (
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                    {t('TemplateLibrary.columnsHint')}
                </Typography>
            )}

            {block.type === EMAIL_BLOCK_TYPE.IMAGE && (
                <>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                        {t('CampaignsPage.coverPhotoFormat')}
                    </Typography>
                    <ImageUploaderSingle
                        extensions=".png, .jpg, .jpeg"
                        maxSize={5 * MB}
                        logoUrl={block.url}
                        disabled={uploading || isView}
                        onDelete={() => onChange({ url: '' })}
                        uploadLogo={uploadImage}
                        isUploading={uploading}
                    />
                    <Input
                        label={t('TemplateLibrary.imageAlt')}
                        fullWidth
                        disabled={isView}
                        inputProps={{
                            value: block.alt || '',
                            onChange: e => onChange({ alt: e.target.value }),
                        }}
                    />
                    <Input
                        label={t('common.link')}
                        fullWidth
                        disabled={isView}
                        inputProps={{
                            value: block.linkUrl || '',
                            onChange: e => onChange({ linkUrl: e.target.value }),
                        }}
                    />
                    <OptionField
                        id="pl-block-image-width"
                        label={t('TemplateLibrary.imageWidth')}
                        value={String(block.widthPercent || 0)}
                        disabled={isView}
                        onChange={value => onChange({ widthPercent: Number(value) || null })}
                        options={[
                            { value: '0', label: t('TemplateLibrary.widthAuto') },
                            { value: '50', label: '50%' },
                            { value: '75', label: '75%' },
                            { value: '100', label: '100%' },
                        ]}
                    />
                    <OptionField
                        id="pl-block-image-radius"
                        label={t('TemplateLibrary.cornerRadius')}
                        value={String(block.radius || 0)}
                        disabled={isView}
                        onChange={value => onChange({ radius: Number(value) || null })}
                        options={[
                            { value: '0', label: t('common.none') },
                            { value: '8', label: '8px' },
                            { value: '16', label: '16px' },
                            { value: '24', label: '24px' },
                        ]}
                    />
                    <AlignField block={block} onChange={onChange} isView={isView} t={t} />
                </>
            )}

            {block.type === EMAIL_BLOCK_TYPE.BUTTON && (
                <>
                    <Input
                        label={t('TemplateLibrary.buttonLabel')}
                        fullWidth
                        disabled={isView}
                        inputProps={{
                            value: block.label || '',
                            onChange: e => onChange({ label: e.target.value }),
                        }}
                    />
                    <Select
                        label={t('TemplateLibrary.buttonDestination')}
                        fullWidth
                        disabled={isView}
                        value={block.binding || EMAIL_BUTTON_BINDING.URL}
                        onChange={({ target: { value } }) => onChange({ binding: value })}
                        options={bindingOptions}
                    />
                    {(block.binding || EMAIL_BUTTON_BINDING.URL) === EMAIL_BUTTON_BINDING.URL ? (
                        <Input
                            label={t('common.link')}
                            fullWidth
                            disabled={isView}
                            inputProps={{
                                value: block.url || '',
                                onChange: e => onChange({ url: e.target.value }),
                            }}
                            addonWithoutWrapper
                            addonComponent={
                                <DynamicFieldsButton
                                    disabled={isView}
                                    onInsert={token => onChange({ url: `${block.url || ''}${token}` })}
                                />
                            }
                        />
                    ) : (
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                            {t(
                                block.binding === EMAIL_BUTTON_BINDING.COUPON
                                    ? 'TemplateLibrary.couponLinkHint'
                                    : 'TemplateLibrary.surveyLinkHint',
                            )}
                        </Typography>
                    )}
                    <ColorPicker
                        label={t('TemplateLibrary.buttonColor')}
                        fullWidth
                        disabled={isView}
                        inputProps={{
                            value: block.color || styles?.buttonColor || '',
                            onChange: e => onChange({ color: e?.target?.value ? e.target.value : e }),
                        }}
                    />
                    <ColorPicker
                        label={t('TemplateLibrary.textColor')}
                        fullWidth
                        disabled={isView}
                        inputProps={{
                            value: block.textColor || '#FFFFFF',
                            onChange: e => onChange({ textColor: e?.target?.value ? e.target.value : e }),
                        }}
                    />
                    <OptionField
                        id="pl-block-button-shape"
                        label={t('TemplateLibrary.buttonShape')}
                        value={block.shape || styles?.buttonShape || 'rounded'}
                        disabled={isView}
                        onChange={value => onChange({ shape: value })}
                        options={EMAIL_BUTTON_SHAPES.map(shape => ({
                            value: shape,
                            label: t(`TemplateLibrary.shape_${shape}`),
                        }))}
                    />
                    <OptionField
                        id="pl-block-button-size"
                        label={t('TemplateLibrary.buttonSize')}
                        value={block.size || 'm'}
                        disabled={isView}
                        onChange={value => onChange({ size: value })}
                        options={SIZE_OPTIONS}
                    />
                    <OptionField
                        id="pl-block-button-width"
                        label={t('TemplateLibrary.buttonWidth')}
                        value={block.width || 'full'}
                        disabled={isView}
                        onChange={value => onChange({ width: value })}
                        options={[
                            { value: 'full', label: t('TemplateLibrary.widthFull') },
                            { value: 'auto', label: t('TemplateLibrary.widthAuto') },
                        ]}
                    />
                    {block.width === 'auto' && <AlignField block={block} onChange={onChange} isView={isView} t={t} />}
                </>
            )}

            {block.type === EMAIL_BLOCK_TYPE.DIVIDER && (
                <>
                    <ColorPicker
                        label={t('TemplateLibrary.lineColor')}
                        fullWidth
                        disabled={isView}
                        inputProps={{
                            value: block.color || '#E0E0E0',
                            onChange: e => onChange({ color: e?.target?.value ? e.target.value : e }),
                        }}
                    />
                    <OptionField
                        id="pl-block-divider-thickness"
                        label={t('TemplateLibrary.thickness')}
                        value={String(block.height || 1)}
                        disabled={isView}
                        onChange={value => onChange({ height: Number(value) })}
                        options={[
                            { value: '1', label: '1px' },
                            { value: '2', label: '2px' },
                            { value: '4', label: '4px' },
                        ]}
                    />
                </>
            )}

            {block.type === EMAIL_BLOCK_TYPE.SPACER && (
                <Input
                    label={t('TemplateLibrary.spacerHeight')}
                    type="number"
                    fullWidth
                    disabled={isView}
                    inputProps={{
                        value: block.height ?? 24,
                        onChange: e => onChange({ height: e.target.value === '' ? null : Number(e.target.value) }),
                        onBlur: () => onChange({ height: Math.max(8, Math.min(160, Number(block.height) || 24)) }),
                    }}
                />
            )}

            {block.type === EMAIL_BLOCK_TYPE.LOGO && (
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                    {t('TemplateLibrary.logoHint')}
                </Typography>
            )}

            {block.type === EMAIL_BLOCK_TYPE.SOCIAL && (
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                    {t('TemplateLibrary.socialHint')}
                </Typography>
            )}

            {(block.type === EMAIL_BLOCK_TYPE.HEADING ||
                block.type === EMAIL_BLOCK_TYPE.TEXT ||
                block.type === EMAIL_BLOCK_TYPE.BUTTON ||
                block.type === EMAIL_BLOCK_TYPE.IMAGE ||
                block.type === EMAIL_BLOCK_TYPE.COLUMNS ||
                block.type === EMAIL_BLOCK_TYPE.DIVIDER) && (
                <OptionField
                    id="pl-block-padding"
                    label={t('TemplateLibrary.padding')}
                    value={block.padding || 'm'}
                    disabled={isView}
                    onChange={value => onChange({ padding: value })}
                    options={SIZE_OPTIONS}
                />
            )}
        </Panel>
    );
};

export default BlockSettings;
